import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  BoundingBoxIcon,
  CheckCircleIcon,
  DotsThreeVerticalIcon,
  UploadIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Card,
  Menu,
  Tooltip,
  type MenuAction,
  ItemIdentifiers,
} from '@skupreme/shared/ui';
import {
  getItemTitle,
  getRemainingQty,
  type FulfillmentItem,
} from '../../lib/shipping-form';
import { catalogItemId, type ShipmentStatus } from '../../lib/shipments';
import { ShipFromSelect, type ProviderOption } from './ShipFromSelect';

/** Where an item may go, and whether the warehouse there stocks it. */
export type ItemDestination = {
  id: string;
  label: string;
  isAvailable?: boolean;
};

export type ShipmentCardProps = {
  title: string;
  items: FulfillmentItem[];
  status: ShipmentStatus;
  /** Only the open shipment holds the form, since a purchase is one at a time. */
  isActive: boolean;
  /** A shipment is going out, and nothing may be rearranged under it. */
  isLocked?: boolean;
  providerId?: string;
  /** Named once shipped, when the warehouse is no longer a choice. */
  providerName?: string;
  /** The warehouses a queued shipment may still be moved to. */
  providerOptions?: ProviderOption[];
  onProviderChange?: (providerId: string) => void;
  /** Hands the shipping options to this shipment, so it is bought before the rest. */
  onActivate?: () => void;
  error?: string;
  isInternational?: boolean;
  isLoading?: boolean;
  /** The provider splits the shipment into this many labels. */
  packageCount?: number;
  /** Own packaging and pre-bought labels only apply to a provider fulfillment. */
  canCustomizePackaging?: boolean;
  onCustomizePackaging?: () => void;
  onAttachLabel?: () => void;
  /** Where a given line may move, asked per line so stock can be shown. */
  destinationsFor?: (itemId: string) => ItemDestination[];
  onMoveItem?: (itemId: string, targetPlanId: string) => void;
  onSplitItem?: (itemId: string) => void;
  onMergeAll?: () => void;
  children?: ReactNode;
};

const STATUS_BADGE: Partial<
  Record<ShipmentStatus, { tone: 'info' | 'success' | 'danger'; label: string }>
> = {
  busy: { tone: 'info', label: 'Shipping' },
  shipped: { tone: 'success', label: 'Shipped' },
  failed: { tone: 'danger', label: 'Failed' },
};

function itemActions({
  destinations,
  canSplit,
}: {
  destinations: ItemDestination[];
  canSplit: boolean;
}): MenuAction[] {
  return [
    ...(canSplit ? [{ id: 'split', label: 'Move to a new shipment' }] : []),
    ...destinations.map((destination, index) => ({
      id: `move:${destination.id}`,
      label: `Move to ${destination.label}`,
      /* Moving to a warehouse without stock is allowed but rarely meant. */
      description: destination.isAvailable ? 'In stock' : 'Out of stock here',
      startsGroup: index === 0 && canSplit,
    })),
  ];
}

function ItemRow({
  item,
  isInternational,
  isEditable,
  destinationsFor,
  canSplit,
  onMoveItem,
  onSplitItem,
  onMatchItem,
}: {
  item: FulfillmentItem;
  isInternational?: boolean;
  isEditable: boolean;
  destinationsFor?: (itemId: string) => ItemDestination[];
  canSplit: boolean;
  onMoveItem?: (itemId: string, targetPlanId: string) => void;
  onSplitItem?: (itemId: string) => void;
  onMatchItem?: (itemId: string) => void;
}) {
  const qty = getRemainingQty(item);
  const shippedQty = item.fulfilledQty || 0;
  const catalogId = catalogItemId(item);
  const missingHsCode = isInternational && !item.hts_code;
  const actions = itemActions({
    destinations: destinationsFor?.(item._id) || [],
    canSplit,
  });

  const runAction = (id: string) => {
    if (id === 'split') {
      onSplitItem?.(item._id);
      return;
    }
    onMoveItem?.(item._id, id.replace('move:', ''));
  };

  return (
    <div className="sk:flex sk:items-center sk:gap-3 sk:py-2">
      {item.p_images?.display_image ? (
        <img
          src={item.p_images.display_image}
          alt=""
          className="sk:size-8 sk:shrink-0 sk:rounded-option sk:object-cover sk:inset-shadow-hairline"
        />
      ) : (
        <span className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-tertiary" />
      )}

      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
        {catalogId ? (
          <Link
            to={`/app/catalogs/catalog/${encodeURIComponent(catalogId)}`}
            className="sk:truncate sk:text-dense sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
            title={getItemTitle(item)}
          >
            {getItemTitle(item)}
          </Link>
        ) : (
          <span
            className="sk:truncate sk:text-dense sk:font-medium sk:text-fg"
            title={getItemTitle(item)}
          >
            {getItemTitle(item)}
          </span>
        )}
        <ItemIdentifiers
          itemTitle={getItemTitle(item)}
          identifiers={[
            { label: 'SKU', value: item.p_sku },
            { label: 'UPC', value: item.upc },
            { label: 'Alias', value: item.alias },
          ]}
        />
        <span className="sk:flex sk:min-w-0 sk:flex-wrap sk:items-center sk:gap-2 sk:text-xs sk:text-fg-tertiary">
          {shippedQty > 0 ? (
            <span className="sk:whitespace-nowrap">
              {shippedQty} of {item.qty} shipped
            </span>
          ) : null}
          {/* Only a warehouse that answered can put a line out of stock. */}
          {item.available === false ? (
            <Badge tone="caution">
              {item.unavailableReason || 'Out of stock'}
            </Badge>
          ) : null}
          {missingHsCode ? (
            <Tooltip content="An international label needs an HS code on every line.">
              <Badge tone="danger">
                <WarningIcon size={12} weight="bold" />
                No HS code
              </Badge>
            </Tooltip>
          ) : null}
        </span>
      </div>

      {/* A line with no catalog item has nothing to ship until it is matched to one. */}
      {onMatchItem && !catalogId ? (
        <Button
          variant="pill"
          size="sm"
          isDisabled={!isEditable}
          onPress={() => onMatchItem(item._id)}
        >
          Match item
        </Button>
      ) : null}

      <span className="sk:text-dense sk:text-fg-secondary">×{qty}</span>

      {isEditable && actions.length > 0 ? (
        <Menu
          aria-label={`Actions for ${getItemTitle(item)}`}
          actions={actions}
          onAction={runAction}
          trigger={
            <Button
              variant="ghost"
              size="sm"
              shape="circle"
              aria-label={`Move ${getItemTitle(item)}`}
            >
              <DotsThreeVerticalIcon size={16} weight="bold" />
            </Button>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * One shipment: the lines it carries, where it ships from and what it costs.
 * Items and fulfillment are the same card because an item belongs to exactly
 * one shipment, and moving it is what changes where it ships from.
 */
export function ShipmentCard({
  title,
  items,
  status,
  isActive,
  isLocked,
  providerId,
  providerName,
  providerOptions = [],
  onProviderChange,
  onActivate,
  error,
  isInternational,
  isLoading,
  packageCount = 0,
  canCustomizePackaging,
  onCustomizePackaging,
  onAttachLabel,
  destinationsFor,
  onMoveItem,
  onSplitItem,
  onMergeAll,
  children,
}: ShipmentCardProps) {
  const badge = STATUS_BADGE[status];
  const isEditable = (status === 'draft' || status === 'failed') && !isLocked;
  /* A lone line cannot be split off: it is already the whole shipment. */
  const canSplit = isEditable && items.length > 1;

  return (
    <Card
      title={
        <span className="sk:flex sk:items-center sk:gap-2">
          {status === 'shipped' ? (
            <CheckCircleIcon
              size={14}
              weight="fill"
              className="sk:text-success"
            />
          ) : null}
          {title}
          <span className="sk:font-normal sk:text-fg-tertiary">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
          {badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : null}
        </span>
      }
      action={
        <span className="sk:flex sk:h-7 sk:items-center sk:gap-1.5">
          {onMergeAll && isActive && isEditable ? (
            <Tooltip content="Ship everything from this warehouse" asChild>
              <Button variant="ghost" size="sm" onPress={onMergeAll}>
                Merge all
              </Button>
            </Tooltip>
          ) : null}
          {canCustomizePackaging &&
          isActive &&
          (onCustomizePackaging || onAttachLabel) ? (
            <>
              {onCustomizePackaging ? (
                <Tooltip content="Customize packaging" asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="circle"
                    aria-label="Customize packaging"
                    onPress={onCustomizePackaging}
                  >
                    <BoundingBoxIcon size={14} weight="duotone" />
                  </Button>
                </Tooltip>
              ) : null}
              {onAttachLabel ? (
                <Tooltip content="Attach a label you already have" asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="circle"
                    aria-label="Attach label"
                    onPress={onAttachLabel}
                  >
                    <UploadIcon size={14} />
                  </Button>
                </Tooltip>
              ) : null}
            </>
          ) : null}
        </span>
      }
      bodyClassName="sk:flex sk:flex-col sk:px-4 sk:py-1"
    >
      {isLoading && items.length === 0 ? (
        <div className="sk:h-16 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      ) : null}

      {items.map(item => (
        <ItemRow
          key={item._id}
          item={item}
          isInternational={isInternational}
          isEditable={isEditable}
          destinationsFor={destinationsFor}
          canSplit={canSplit}
          onMoveItem={onMoveItem}
          onSplitItem={onSplitItem}
        />
      ))}

      {packageCount > 1 ? (
        <span className="sk:border-t sk:border-rule sk:py-2 sk:text-xs sk:text-fg-tertiary">
          Ships as {packageCount} labels.
        </span>
      ) : null}

      {error ? (
        <span className="sk:border-t sk:border-rule sk:py-2 sk:text-dense sk:text-danger">
          {error}
        </span>
      ) : null}

      {status === 'shipped' ? (
        <span className="sk:border-t sk:border-rule sk:py-3 sk:text-dense sk:text-fg-tertiary">
          Shipped from {providerName || 'the selected warehouse'}.
        </span>
      ) : isActive ? (
        children
      ) : (
        /* Waiting its turn: the warehouse is already a choice, and the shipping
           options come here as soon as this is the shipment being bought. */
        <div className="sk:flex sk:flex-col sk:gap-1.5 sk:border-t sk:border-rule sk:py-3">
          <span className="sk:text-xs sk:font-medium sk:text-fg-secondary">
            Ship from
          </span>
          <ShipFromSelect
            options={providerOptions}
            value={providerId}
            isDisabled={!isEditable}
            onChange={id => onProviderChange?.(id)}
          />
          {onActivate && isEditable ? (
            <Button
              variant="pill"
              size="md"
              className="sk:mt-1 sk:w-fit"
              onPress={onActivate}
            >
              Ship this one first
            </Button>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export type UnstockedItemsCardProps = {
  items: FulfillmentItem[];
  destinationsFor: (itemId: string) => ItemDestination[];
  isLocked?: boolean;
  onMoveItem: (itemId: string, targetPlanId: string) => void;
  onSplitItem: (itemId: string) => void;
  /** Opens the catalog match for a line with no catalog item. */
  onMatchItem?: (itemId: string) => void;
  /** What the operator does about these lines, under them. */
  note?: string;
};

/**
 * Lines no shipment can carry, because no warehouse reports stock for them.
 * Listed rather than hidden: they are still owed on the order, and they can be
 * forced into a shipment if the stock figure is wrong.
 */
export function UnstockedItemsCard({
  items,
  destinationsFor,
  isLocked,
  onMoveItem,
  onSplitItem,
  onMatchItem,
  note,
}: UnstockedItemsCardProps) {
  if (items.length === 0) {
    return null;
  }

  /* Saying "nowhere" is wrong when every line is merely held in the wrong warehouse. */
  const sharedReason = items.every(
    item =>
      item.unavailableReason &&
      item.unavailableReason === items[0].unavailableReason,
  )
    ? items[0].unavailableReason
    : undefined;

  return (
    <Card
      title={sharedReason || 'Unavailable at all locations'}
      bodyClassName="sk:flex sk:flex-col sk:px-4 sk:py-1"
    >
      {items.map(item => (
        <ItemRow
          key={item._id}
          item={item}
          isEditable={!isLocked}
          destinationsFor={destinationsFor}
          canSplit={!isLocked && items.length > 1}
          onMoveItem={onMoveItem}
          onSplitItem={onSplitItem}
          onMatchItem={onMatchItem}
        />
      ))}
      {note ? (
        <span className="sk:border-t sk:border-rule sk:py-3 sk:text-dense sk:text-fg-tertiary">
          {note}
        </span>
      ) : null}
    </Card>
  );
}
